/**
 * Helper functions to normalize content from Plone
 * for the Design System components.
 */
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import config from '@plone/volto/registry';
import Video from '@plone/volto/components/manage/Blocks/Video/Body';
import { getReference, getItemReference } from '@fhnw/helpers/Url/UrlHelper';
import { generateImageSizes } from './imageSizes';
import { ImageSizes } from './types';

const RENAME_KEYS: Record<string, string> = {
  discreet: 'small',
  format: 'aspect',
  image_title: 'title',
  image_alt_text: 'alt',
  Title: 'title',
  Description: 'description',
};

const IGNORE_KEYS: Array<string> = ['@id', '@type', 'media_type'];

export const normalizeLink = (link?: string | object | null) => {
  if (typeof link === 'undefined' || link === null) {
    return '';
  }
  if (typeof link === 'string') {
    return getReference(link);
  }
  return getItemReference(link as Content);
};

const normalizeKey = (key: string) => {
  return RENAME_KEYS[key] || key;
};

export const normalizeContent = (
  content: any,
  for_display: boolean = false,
  imageSizes?: ImageSizes,
): string | Record<string, any> | string[] | Record<string, any>[] => {
  if (Array.isArray(content)) {
    return content.map((item) =>
      normalizeContent(item, for_display, imageSizes),
    );
  }

  if (typeof content !== 'object' || !content) {
    return content;
  }

  if (content?.token) {
    return for_display ? content?.title || content?.value : content.token;
  }

  const Image = config.getComponent({ name: 'Image' }).component;

  const normalized: Record<string, any> = {};
  const keys = Object.keys(content);
  for (const i in keys) {
    let key = keys[i];
    if (IGNORE_KEYS.includes(key)) {
      continue;
    }
    let value = content[key];
    if (key === 'new_tab') {
      if (value) {
        normalized.target = '_blank';
      }
      continue;
    }
    if (key === 'url') {
      key = 'href';
      value = normalizeLink(value);
    }
    if (key === 'image') {
      if (value?.image_scales || value?.image) {
        normalized['children'] = (
          <Image
            item={value}
            loading="lazy"
            className="media__object"
            alt={content?.image_alt_text || value?.title || ''}
            imageField={value?.image_field || 'image'}
            sizes={generateImageSizes(imageSizes)}
          />
        );
      }
      continue;
    }
    if (key === 'image_field' && value && content?.['image_scales']) {
      value = value.replace(/_link$/, '');
      normalized['image'] = {
        focus_point: normalizeContent(content?.[`${value}_focus_point`], false),
        aspect: normalizeContent(content?.[`${value}_format`], false),
        children: (
          <Image
            item={content}
            loading="lazy"
            className="media__object"
            alt={content?.image_alt_text || content?.title || ''}
            imageField={content?.image_field || 'image'}
            sizes={generateImageSizes(imageSizes)}
          />
        ),
      };
    }
    if (key === 'image_field' && !value && content?.['image_scales']) {
      // Skip opengraph_image: it is only for social-media meta tags, never a teaser image.
      const imageField = Object.keys(content.image_scales).find(
        (field) => field !== 'opengraph_image',
      );
      if (!imageField) {
        continue;
      }
      value = imageField.replace(/_link$/, '');
      normalized['image'] = {
        focus_point: normalizeContent(content?.[`${value}_focus_point`], false),
        aspect: normalizeContent(content?.[`${value}_format`], false),
        children: (
          <Image
            item={content}
            loading="lazy"
            className="media__object"
            alt={content?.image_alt_text || content?.title || ''}
            imageField={imageField}
            sizes={generateImageSizes(imageSizes)}
          />
        ),
      };
      continue;
    }
    if (key === 'preview_image_link' && value?.['image_scales']) {
      key = key.replace(/_link$/, '');
      normalized['image'] = {
        focus_point: normalizeContent(content?.[`${key}_focus_point`], false),
        aspect: normalizeContent(content?.[`${key}_format`], false),
        children: (
          <Image
            item={value}
            loading="lazy"
            className="media__object"
            alt={value?.image_alt_text || content?.title || ''}
            imageField={value?.image_field || 'image'}
            sizes={generateImageSizes(imageSizes)}
          />
        ),
      };
      continue;
    }
    if (
      key === 'video_url' &&
      value &&
      normalizeContent(content?.['media_type']) === 'external_video'
    ) {
      normalized['children'] = (
        <div className="media__object media__object--embed">
          <Video
            data={{
              url: value,
              title: content?.image_title || content?.title || '',
            }}
            autoplay={content?.['autoplay']}
            controls={!content?.['hide_controls']}
            forceCaptions={!!content?.['force_captions']}
            title={content?.image_title || content?.title || ''}
          />
        </div>
      );
      continue;
    }
    if (
      key === 'video' &&
      value &&
      normalizeContent(content?.['media_type']) === 'video'
    ) {
      normalized['children'] = (
        <div className="media__object media__object--embed">
          <Video
            data={{
              url: value['@id'],
              title: content?.image_title || content?.title || '',
            }}
            autoplay={content?.['autoplay']}
            controls={!content?.['hide_controls']}
            forceCaptions={!!content?.['force_captions']}
            title={content?.image_title || content?.title || ''}
          />
        </div>
      );
      continue;
    }
    normalized[normalizeKey(key)] = normalizeContent(
      value,
      for_display,
      imageSizes,
    );
  }

  return normalized;
};
